import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const POPULAR = ['Delhi', 'Mumbai', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Ahmedabad'];

// Routes where the popup should never show (dashboards, auth, legal, etc.)
const HIDDEN_PATH_PREFIXES = [
  '/admin', '/organizer', '/player-dashboard', '/profile',
  '/login', '/register', '/forgot-password', '/reset-password', '/verify-email',
  '/privacy', '/terms'
];

const NearYouPopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [showCity, setShowCity] = useState(false);

  const onHiddenRoute = HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (user || onHiddenRoute) {
      setShow(false);
      return;
    }
    const dismissed = localStorage.getItem('nearYouDismissed');
    const savedCity = localStorage.getItem('userCity');
    if (!dismissed && !savedCity) {
      const t = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(t);
    }
  }, [user, onHiddenRoute]);

  if (user || onHiddenRoute) return null;

  const allowLoc = () => {
    if (!navigator.geolocation) { setShowCity(true); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`);
        const d = await r.json();
        const city = d.address?.city || d.address?.town || d.address?.state_district;
        if (city) {
          localStorage.setItem('userCity', city);
          setShow(false);
          navigate(`/tournaments?city=${encodeURIComponent(city)}`);
        } else setShowCity(true);
      } catch (e) { setShowCity(true); }
    }, () => setShowCity(true), { timeout: 8000 });
  };

  const pick = (c) => {
    localStorage.setItem('userCity', c);
    setShow(false);
    navigate(`/tournaments/city/${c.toLowerCase()}`);
  };

  const dismiss = () => { localStorage.setItem('nearYouDismissed', 'true'); setShow(false); };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 z-40 md:max-w-sm bg-white border border-gray-200 rounded-2xl shadow-2xl p-5 animate-fade-up" data-testid="near-you-popup">
      <button onClick={dismiss} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1" data-testid="dismiss-popup-btn" aria-label="Dismiss">
        <X size={16} weight="bold" />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
          <MapPin size={18} weight="bold" className="text-[#F97316]" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-sm mb-0.5">Find tournaments near you</h3>
          <p className="text-xs text-gray-500">Discover basketball in your city</p>
        </div>
      </div>

      {!showCity ? (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={allowLoc} className="btn-primary px-3 py-2 rounded-lg text-xs font-semibold" data-testid="allow-location-btn">Use Location</button>
          <button onClick={() => setShowCity(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-3 py-2 rounded-lg text-xs font-semibold transition-colors" data-testid="select-city-btn">Pick City</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          {POPULAR.map(c => (
            <button key={c} onClick={() => pick(c)} className="bg-gray-50 hover:bg-[#F97316] hover:text-white text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-testid={`city-${c.toLowerCase()}-btn`}>
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NearYouPopup;
